/**
 * Store dos catálogos de contrato (CA3 — Central de Catálogos do módulo Clientes).
 *
 * Interface com /api/v1/contrato-catalogos: CRUD dos 5 catálogos de contrato
 * (empresas parceiras, índices de reajuste, SLAs, periodicidades, tipos) +
 * a antecedência dos alertas (KV). Mesmo padrão de `r-import-catalogs-store`:
 * useQuery pra list + mutations com invalidação automática.
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type CatalogoContratoSlug =
  | 'empresas-parceiras'
  | 'indices-reajuste'
  | 'slas'
  | 'periodicidades'
  | 'tipos';

export interface ContratoCatalogoItem {
  id: string;
  nome: string;
  ativo: boolean;
  ordem: number;
  criadoEmIso: string;
  /** Nos prazos (SLA): o NÚMERO, na unidade de `unidade`. */
  horasUteis?: number;
  /** Nos prazos (SLA): horas de expediente ou dias úteis. */
  unidade?: SlaUnidade;
  escopoDetalhado?: boolean;
}

export type SlaUnidade = 'horas_expediente' | 'dias_uteis';

export interface ContratoCatalogoInput {
  nome?: string;
  ordem?: number;
  ativo?: boolean;
  horasUteis?: number;
  unidade?: SlaUnidade;
  escopoDetalhado?: boolean;
}

export function useContratoCatalogo(
  slug: CatalogoContratoSlug,
  opts: { includeInactive?: boolean } = {},
) {
  const qs = opts.includeInactive ? '?includeInactive=true' : '';
  return useQuery({
    queryKey: ['v1', 'contrato-catalogos', slug, opts.includeInactive ?? false] as const,
    queryFn: () => api.get<ContratoCatalogoItem[]>(`/api/v1/contrato-catalogos/${slug}${qs}`),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useContratoCatalogoActions(slug: CatalogoContratoSlug) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['v1', 'contrato-catalogos', slug] });

  const create = useMutation({
    mutationFn: (input: ContratoCatalogoInput) =>
      api.post<ContratoCatalogoItem>(`/api/v1/contrato-catalogos/${slug}`, input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ContratoCatalogoInput }) =>
      api.put<ContratoCatalogoItem>(`/api/v1/contrato-catalogos/${slug}/${id}`, input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true }>(`/api/v1/contrato-catalogos/${slug}/${id}`),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

// ─── Antecedência dos alertas (KV) ─────────────────────────────────────────

export interface AlertaContratoConfig {
  ativo: boolean;
  reajusteAntecedenciaDias: number;
  avisoPrevioAntecedenciaDias: number;
}

export function useAlertaContratoConfig() {
  return useQuery({
    queryKey: ['v1', 'contrato-catalogos', 'alertas-config'] as const,
    queryFn: () => api.get<AlertaContratoConfig>('/api/v1/contrato-catalogos/alertas-config'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useAlertaContratoConfigActions() {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (input: Partial<AlertaContratoConfig>) =>
      api.put<AlertaContratoConfig>('/api/v1/contrato-catalogos/alertas-config', input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['v1', 'contrato-catalogos', 'alertas-config'] }),
  });
  return { save };
}
